import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, MailCheck, Send, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderText = (text) => {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) {
      const [, label, href] = m;
      return href.startsWith("/") ? (
        <Link key={i} to={href} className="font-semibold text-royal underline underline-offset-2 hover:text-royal-dark">
          {label}
        </Link>
      ) : (
        <a key={i} href={href} className="font-semibold text-royal underline underline-offset-2 hover:text-royal-dark">
          {label}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const GREETING = {
  role: "assistant",
  content:
    "Hi, I'm June — your moving concierge at Oregon Tote Rentals. Tell me about your move (home size, town, dates) and I'll point you to the right package, or ask me anything about pricing, delivery and pickup.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", details: "" });
  const [leadBusy, setLeadBusy] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  if (!sessionRef.current) {
    sessionRef.current =
      localStorage.getItem("otr_chat_session") ||
      (() => {
        const id = crypto.randomUUID();
        localStorage.setItem("otr_chat_session", id);
        return id;
      })();
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ") || ev === "data: [DONE]") continue;
          const { t } = JSON.parse(ev.slice(6));
          if (t) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + t };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "I hit a snag on my end — try again in a moment, or call us at (541) 408-7019.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const sendLead = async (e) => {
    e.preventDefault();
    setLeadBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/chat/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionRef.current, ...lead }),
      });
      if (!resp.ok) throw new Error();
      setLeadOpen(false);
      setLead({ name: "", email: "", phone: "", details: "" });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Done — your details are with David's team, and they'll email your personalized quote shortly. Anything else I can help with in the meantime?" },
      ]);
    } catch {
      toast.error("Couldn't send that — try again or email reservations@oregon-tote-rentals.com");
    } finally {
      setLeadBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-button"
        aria-label="Chat with June"
        className="fixed bottom-6 right-6 z-[90] grid h-14 w-14 place-items-center rounded-full bg-royal text-white shadow-xl shadow-royal/30 transition-all duration-200 hover:scale-105 hover:bg-royal-dark print:hidden"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-4 z-[90] flex h-[480px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl sm:right-6 print:hidden"
        >
          <div className="flex items-center justify-between gap-3 bg-ink px-5 py-4 text-white">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-royal">
                <Sparkles className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-sm font-bold">June</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/60">
                  Moving concierge · replies instantly
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLeadOpen(!leadOpen)}
                data-testid="chat-quote-button"
                className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:bg-gold hover:text-ink"
              >
                <MailCheck className="h-3.5 w-3.5" /> Email me a quote
              </button>
              <button
                onClick={() => { setOpen(false); setLeadOpen(false); }}
                data-testid="chat-close-button"
                aria-label="Close chat"
                className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white transition-colors duration-200 hover:bg-white/25"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          {leadOpen && (
            <form onSubmit={sendLead} className="space-y-2 border-b border-slate-100 bg-white p-4" data-testid="chat-lead-form">
              <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })}
                     placeholder="Your name *" required data-testid="quote-name-input"
                     className="h-10 w-full rounded-xl border border-slate-200 bg-paper px-3 text-sm outline-none focus:border-royal" />
              <div className="flex gap-2">
                <input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })}
                       type="email" placeholder="Email *" required data-testid="quote-email-input"
                       className="h-10 w-full rounded-xl border border-slate-200 bg-paper px-3 text-sm outline-none focus:border-royal" />
                <input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                       placeholder="Phone" data-testid="quote-phone-input"
                       className="h-10 w-28 rounded-xl border border-slate-200 bg-paper px-3 text-sm outline-none focus:border-royal" />
              </div>
              <textarea value={lead.details} onChange={(e) => setLead({ ...lead, details: e.target.value })}
                        placeholder="Move details — home size, towns, dates (optional)" rows={2}
                        data-testid="quote-details-input"
                        className="w-full resize-none rounded-xl border border-slate-200 bg-paper px-3 py-2 text-sm outline-none focus:border-royal" />
              <button type="submit" disabled={leadBusy} data-testid="quote-submit-button"
                      className="w-full rounded-xl bg-ink py-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-royal disabled:opacity-50">
                {leadBusy ? "Sending…" : "Send to the team"}
              </button>
            </form>
          )}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-paper px-4 py-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={m.role === "assistant" ? `chat-assistant-message-${i}` : `chat-user-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-royal text-white"
                      : "border border-slate-200 bg-white text-ink"
                  }`}
                >
                  {m.role === "assistant" ? renderText(m.content) : m.content}
                  {busy && i === messages.length - 1 && m.role === "assistant" && !m.content && (
                    <span className="inline-flex gap-1 py-1">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300" style={{ animationDelay: "0.15s" }} />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300" style={{ animationDelay: "0.3s" }} />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 border-t border-slate-100 bg-white p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about packages, pricing, delivery…"
              data-testid="chat-input"
              className="h-11 flex-1 rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              data-testid="chat-send-button"
              aria-label="Send message"
              className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-royal text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
